import type {
    AddNoteLineItemInput,
    DeleteNoteLineItemInput,
    SalesDraftDetailOutput,
    SalesDraftNoteLineItem,
    UpdateNoteLineItemInput,
} from '@/server/types/drafts-types';
import { trpcClient } from '@/utils/trpc-client';
import type { ActorRefFrom } from 'xstate';
import { assertEvent, assign, fromPromise, sendTo, setup } from 'xstate';
import type { draftMachine } from './draft-machine';

export const addNoteItemMachine = setup({
    types: {
        input: {} as {
            draft_order_id: string;
        },
        context: {} as {
            test: string;
            draft_order_id: string;
            draftOrder?: SalesDraftDetailOutput['draft_order'];
        },
        output: {} as {
            draftOrder?: SalesDraftDetailOutput['draft_order'];
        },
        events: {} as {
            type: 'submit.note';
            payload: {
                text: string;
            };
        },
    },
    actors: {
        addNoteLineItem: fromPromise<SalesDraftDetailOutput, AddNoteLineItemInput>(({ input }) =>
            trpcClient.platoUser.command.salesDraft.lineItems.addNoteLineItem.mutate(input),
        ),
    },
    guards: {
        isDirty: ({ context, event }) => {
            assertEvent(event, 'submit.note');

            return !!event.payload.text && context.test !== event.payload.text;
        },
    },
}).createMachine({
    context: ({ input }) => ({
        test: '',
        draft_order_id: input.draft_order_id,
    }),
    initial: 'editing',
    states: {
        editing: {
            on: {
                'submit.note': {
                    guard: 'isDirty',
                    target: 'submitting',
                },
            },
        },
        submitting: {
            invoke: {
                id: 'addNoteLineItem',
                src: 'addNoteLineItem',
                input: ({ context, event }) => {
                    assertEvent(event, 'submit.note');

                    const { payload } = event;

                    return {
                        note: payload.text,
                        draft_order_id: context.draft_order_id,
                        external: {},
                    };
                },
                onDone: {
                    target: 'submitted',
                    actions: assign({
                        draftOrder: ({ event }) => event.output.draft_order,
                    }),
                },
                onError: {
                    target: 'error',
                },
            },
        },
        submitted: {
            type: 'final',
        },
        error: {
            after: {
                500: 'editing',
            },
        },
    },
    output: ({ context }) => ({
        draftOrder: context.draftOrder,
    }),
});

export const noteLineItemMachine = setup({
    types: {
        context: {} as {
            noteLineItem: SalesDraftNoteLineItem;
            draftOrderId: string;
            parentRef: ActorRefFrom<typeof draftMachine>;
        },
        events: {} as
            | {
                  type: 'update.note';
                  payload: {
                      text: string;
                  };
              }
            | {
                  type: 'focus.note';
              }
            | {
                  type: 'delete.item';
              },
        input: {} as {
            noteLineItem: SalesDraftNoteLineItem;
            draftOrderId: string;
            parentRef: ActorRefFrom<typeof draftMachine>;
        },
    },
    guards: {
        isDirty: ({ context, event }) => {
            assertEvent(event, 'update.note');

            return context.noteLineItem.note !== event.payload.text;
        },
    },
    actors: {
        updateNoteLineItem: fromPromise<SalesDraftDetailOutput, UpdateNoteLineItemInput>(({ input }) =>
            trpcClient.platoUser.command.salesDraft.lineItems.updateNoteLineItem.mutate(input),
        ),
        deleteNoteLineItem: fromPromise<SalesDraftDetailOutput, DeleteNoteLineItemInput>(({ input }) =>
            trpcClient.platoUser.command.salesDraft.lineItems.deleteNoteLineItem.mutate(input),
        ),
    },
    actions: {
        itemUpdated: sendTo(
            ({ context }) => context.parentRef,
            ({ context }) => ({
                type: 'lineItems.updated',
                payload: {
                    line_item_id: context.noteLineItem.line_item_id,
                },
            }),
        ),
    },
}).createMachine({
    id: 'noteLineItemMachine',
    context: ({ input }) => ({
        noteLineItem: input.noteLineItem,
        draftOrderId: input.draftOrderId,
        parentRef: input.parentRef,
    }),
    initial: 'reading',
    states: {
        reading: {
            on: {
                'focus.note': {
                    target: 'editing',
                },
                'delete.item': {
                    target: 'deleting',
                },
            },
        },
        editing: {
            on: {
                'update.note': [
                    {
                        guard: 'isDirty',
                        target: 'updating',
                        actions: sendTo(
                            ({ context }) => context.parentRef,
                            ({ context }) => ({
                                type: 'lineItems.updating',
                                payload: {
                                    line_item_id: context.noteLineItem.line_item_id,
                                },
                            }),
                        ),
                    },
                    {
                        target: 'reading',
                    },
                ],
            },
        },
        updating: {
            invoke: {
                id: 'updateNoteLineItem',
                src: 'updateNoteLineItem',
                input: ({ context, event }) => {
                    assertEvent(event, 'update.note');

                    const { payload } = event;
                    const { noteLineItem, draftOrderId } = context;

                    return {
                        note: payload.text,
                        draft_order_id: draftOrderId,
                        draft_line_item_id: noteLineItem.line_item_id,
                        external: noteLineItem.external,
                    };
                },
                onDone: {
                    target: 'updated',
                    actions: [
                        {
                            type: 'itemUpdated',
                        },
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => ({
                                type: 'lineItems.update.draftOrder',
                                payload: event.output.draft_order,
                            }),
                        ),
                    ],
                },
                onError: {
                    target: 'error',
                    actions: [
                        {
                            type: 'itemUpdated',
                        },
                    ],
                },
            },
        },
        updated: {
            after: {
                500: 'reading',
            },
        },
        deleting: {
            invoke: {
                id: 'deleteNoteLineItem',
                src: 'deleteNoteLineItem',
                input: ({ context }) => {
                    const { noteLineItem, draftOrderId } = context;

                    return {
                        draft_order_id: draftOrderId,
                        draft_line_item_id: noteLineItem.line_item_id,
                    };
                },
                onDone: {
                    target: 'deleted',
                    actions: [
                        sendTo(
                            ({ context }) => context.parentRef,
                            ({ event }) => {
                                return {
                                    type: 'lineItems.update.draftOrder',
                                    payload: event.output.draft_order,
                                };
                            },
                        ),
                    ],
                },
                onError: {
                    target: 'error',
                },
            },
        },
        deleted: {
            type: 'final',
        },
        error: {
            after: {
                500: {
                    target: 'editing',
                },
            },
        },
    },
});
